import { AlertCircle, AlertTriangle, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AvailabilityDot } from '@/components/atoms';
import { PatientAvatar, ScripsIcon, type ScripsIconName } from '@/shared/ui/components';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuthStore } from '@/auth/auth-store';
import type {
  CreateAppointmentProfileRequest,
  ProviderIssue,
  SlotResponse,
} from '@/types/scheduling-api';
import { SlotSelectionPopover } from '@/scheduling/components/slot-selection-popover/slot-selection-popover';
import { pickPreferredProfile } from './pick-preferred-profile';
import { NO_PRACTICE_PROFILE_TEXT } from '@/scheduling/lib/practice-slot-profile';
import { QuickBookChip } from './quick-book-chip';
import { ClinicStamp } from './clinic-stamp';

/**
 * ProviderCard — Component
 * Horizontal provider summary shown in the scheduling provider strip.
 * Shows name, specialty, availability, next slots, and quick-book chips.
 *
 * Quick-book chips mirror Flutter's `quickBookCardView` — each chip is one
 * location-group of appointment profiles, not one profile-per-chip. The
 * chip's icon comes from the location category; the icon color comes from
 * the first profile's `color` field.
 *
 * FHIR mapping (documentation — Stream 1 uses DTOs):
 *   name → Practitioner.name
 *   specialty → PractitionerRole.specialty
 *   available → Schedule.active
 *   slots → Slot.start (filtered by status=free)
 *   quickBookGroups → grouping of HealthcareService / Schedule.appointmentType
 */

/** A group of appointment profiles sharing the same location category. */
export interface QuickBookGroup {
  /** "office" | "home" | "virtual" | "block" — drives the chip icon. */
  location: string;
  /** Duration shown on the chip (first profile's `minutesDuration`). */
  durationMinutes: number;
  /** Hex color for the chip icon (from first profile's `color`). */
  color?: string;
  /** All profiles in this group — listed in the click popover. */
  profiles: CreateAppointmentProfileRequest[];
}

interface ProviderCardProps {
  name: string;
  specialty: string;
  initials: string;
  /** Avatar URL — falls back to initials when missing or load fails. */
  photo?: string;
  /** Credential/license issue (PROD-980) — renders a red/amber badge +
   *  tooltip next to the name. No badge when absent. */
  issue?: ProviderIssue | null;
  available: boolean;
  slotsToday: number;
  schedule: string;
  nextSlots: string[];
  /**
   * Raw slot objects aligned 1:1 with `nextSlots`. When present, the
   * "Next" inline chips become clickable triggers that fire
   * `onSelectSlot(slot, preferredProfile)` — PROD-829 quick-create path B
   * (Flutter `calendar_tab.dart:4514, 4552, 4615`). When omitted, the
   * chips stay as plain text (degrades gracefully on data sources
   * that haven't carried raw slots through).
   */
  nextSlotResponses?: import('@/types/scheduling-api').AppointmentSlot[];
  quickBookGroups: QuickBookGroup[];
  /** Whether this provider is in the user's checked set (✓ in the strip). */
  isSelected?: boolean;
  /** Called when the checkbox next to the name is clicked. */
  onToggleSelect?: () => void;
  /**
   * Called when the user picks a profile inside the chip popover.
   * Receives the group + the chosen profile so the consumer can open
   * the CreateAppointmentPanel pre-filled.
   */
  onSelectProfile?: (group: QuickBookGroup, profile: CreateAppointmentProfileRequest) => void;
  /**
   * The hover card's "Book" buttons (In clinic / Video / Home): opens Create with the provider and that LOCATION only,
   * no profile picked (Antonin, 15 Oct: "it already selected a random Appointment profile, it should not"). `profile`
   * is the group's reference profile, used only to resolve the location and the provider's specialty.
   * Falls back to `onSelectProfile` when absent.
   */
  onBookLocation?: (group: QuickBookGroup, profile: CreateAppointmentProfileRequest) => void;
  onClick?: () => void;
  /**
   * Fired when the provider NAME is clicked — opens that provider's settings
   * page. Flutter parity: `calendar_tab.dart:4215-4238` wraps ONLY the name
   * (+ issue icon) in an `InkWell` whose `onTap` pushes `DoctorBaseWidget`;
   * the avatar, selection checkbox, slot links, and quick-book chips are all
   * OUTSIDE it. Provided only when the user may open provider settings
   * (`Manage_Doctors` view) — otherwise the name renders as plain text.
   */
  onNameClick?: () => void;
  /**
   * Provider id — required to wire the slot-count popover (PROD-828).
   * Different providers ship from the strip; varies per card, so this
   * is the one popover-related value that genuinely needs to be a prop.
   * `practiceId` is derived from `preferredProfile.practiceId` (Flutter
   * `calendar_tab.dart:4324` uses the same source) and `organizationId`
   * is read from `useAuthStore` (matches every appointment-panel section
   * — see Samer review §4 on PR #213).
   */
  practitionerId?: string;
  /**
   * `?x=calendar-practice-slots` only: the calendar's VIEWED practice. When
   * set, the slot popover and the Next chips use this practice and
   * `slotProfile` (THE ONE RULE, `practice-slot-profile.ts`) instead of the
   * preferred profile's own practice. Absent = today's behaviour.
   */
  slotPracticeId?: string;
  /** With `slotPracticeId`: the chosen profile at that practice; null = none there. */
  slotProfile?: CreateAppointmentProfileRequest | null;
  /**
   * With `slotPracticeId`: the calendarSlots feed is loading ('loading') or failed ('error'). The card shows no
   * count, no On shift / Off today and no "No appointment profile at this practice" (that needs a successful
   * answer); 'error' shows "Couldn't load slots" and Retry (`onRetrySlots`).
   */
  slotsState?: 'loading' | 'error';
  /** Retry for `slotsState: 'error'`: asks the calendarSlots feed again. */
  onRetrySlots?: () => void;
  /** With `slotsState: 'error'`: the Retry request is running. Shows "— Loading…" and disables Retry. */
  slotsRetrying?: boolean;
  /**
   * Fired when the user picks a slot from the popover. The consumer
   * decides whether to open Create Appointment pre-filled or do
   * something else. Mirrors Flutter's `onQuickCreate(slot, profile, doctor)`.
   */
  onSelectSlot?: (slot: SlotResponse, profile: CreateAppointmentProfileRequest) => void;
  /**
   * 'strip' (default): the original horizontal card. 'detail': the cleaner card the calendar's mini chips reveal on
   * hover (Antonin, 13 Oct) — same actions, laid out as labelled rows.
   */
  layout?: 'strip' | 'detail';
}

export function ProviderCard({
  name,
  specialty,
  initials: _initials,
  photo,
  issue,
  available,
  slotsToday,
  schedule,
  nextSlots,
  nextSlotResponses,
  quickBookGroups,
  isSelected = false,
  onToggleSelect,
  onSelectProfile,
  onBookLocation,
  onClick,
  onNameClick,
  practitionerId,
  slotPracticeId,
  slotProfile,
  slotsState,
  onRetrySlots,
  slotsRetrying = false,
  onSelectSlot,
  layout = 'strip',
}: ProviderCardProps) {
  // Read org/practice IDs from auth store — matches the pattern used
  // by every appointment-panel section (location-selector, patient-
  // section, provider-selection-view, etc.). Samer review §4 on PR #213
  // — previous prop-drilling diverged from this convention.
  const organizationId = useAuthStore((s) => s.user?.organizationId);
  const practiceName = useAuthStore((s) => s.user?.practiceName);
  // Switch on (`slotPracticeId` set): the viewed practice + the profile THE ONE RULE chose there. Switch off: today's
  // behaviour — the preferred profile and ITS practice (Flutter `calendar_tab.dart:4324`), which asked another
  // practice for slots when the preferred profile lived elsewhere (Samer's bug, 19 Oct).
  const practiceScoped = !!slotPracticeId;
  const preferredProfile = practiceScoped ? (slotProfile ?? null) : pickPreferredProfile(quickBookGroups);
  const popoverPracticeId = practiceScoped ? slotPracticeId : (preferredProfile?.practiceId ?? null);
  // Feed not in yet or failed: we do not know the count, the shift or whether a profile exists.
  const slotsUnknown = practiceScoped && !!slotsState;
  const slotsFailed = slotsUnknown && slotsState === 'error';
  const noPracticeProfile = practiceScoped && !slotsUnknown && !preferredProfile;
  const slotPopoverEnabled =
    slotsToday > 0 &&
    !!practitionerId &&
    !!popoverPracticeId &&
    !!organizationId &&
    !!preferredProfile?.id;
  // The pieces, shared by both layouts so every action (settings link, filter ✓, slot popover, Next quick-create,
  // quick-book chips) behaves the same in the strip and in the hover card.
  const nameEl = onNameClick ? (
    // Name-only click target → provider settings (Flutter InkWell,
    // calendar_tab.dart:4215). stopPropagation so a future whole-card
    // onClick can't double-fire. Underline-on-hover hints it's a link
    // without recolouring (Flutter keeps the name's normal text color).
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onNameClick();
      }}
      title="Open provider settings"
      className="text-sm font-medium text-text-primary truncate leading-snug text-left hover:underline focus:outline-none focus-visible:underline cursor-pointer"
    >
      {name}
    </button>
  ) : (
    <p className="text-sm font-medium text-text-primary truncate leading-snug">{name}</p>
  );
  // Credential/license issue badge (PROD-980) — Flutter `calendar_tab.dart:4255-4273`: error→red AlertCircle,
  // else→amber AlertTriangle; tooltip = issue.message. Only when a message is present.
  const issueEl = issue?.message ? (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className="flex-shrink-0 inline-flex"
            aria-label={issue.message}
            onClick={(e) => e.stopPropagation()}
          />
        }
      >
        {issue.type === 'error' ? (
          <AlertCircle className="h-4 w-4 text-status-error" aria-hidden />
        ) : (
          <AlertTriangle className="h-4 w-4 text-status-warning" aria-hidden />
        )}
      </TooltipTrigger>
      <TooltipContent>{issue.message}</TooltipContent>
    </Tooltip>
  ) : null;
  const checkboxEl = onToggleSelect ? (
    <span
      role="checkbox"
      aria-checked={isSelected}
      aria-label={isSelected ? 'Deselect provider' : 'Select provider'}
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onToggleSelect();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onToggleSelect();
        }
      }}
      className="cursor-pointer flex-shrink-0"
    >
      {isSelected ? (
        <CheckCircle2 className="h-4 w-4 text-brand-primary fill-brand-primary [&>path]:stroke-white" aria-hidden />
      ) : (
        <Circle className="h-4 w-4 text-text-hint hover:text-brand-primary" aria-hidden />
      )}
    </span>
  ) : null;
  const retryEl = onRetrySlots ? (
    <Button
      variant="link"
      size="xs"
      className="h-auto p-0"
      aria-label="Retry loading slots"
      aria-busy={slotsRetrying || undefined}
      disabled={slotsRetrying}
      onClick={(e) => {
        e.stopPropagation();
        onRetrySlots();
      }}
    >
      Retry
    </Button>
  ) : null;
  const slotsErrorEl = slotsFailed ? (
    <span data-slots-load-error className="inline-flex items-center gap-2">
      {slotsRetrying ? (
        <span className="text-text-muted">— Loading…</span>
      ) : (
        <span className="text-status-error">Couldn't load slots</span>
      )}
      {retryEl}
    </span>
  ) : null;
  const slotsEl = slotsFailed ? (
    slotsErrorEl
  ) : slotsUnknown ? (
    <span className="text-text-muted">— slots</span>
  ) : slotPopoverEnabled && preferredProfile && popoverPracticeId && organizationId ? (
      <SlotSelectionPopover
        practitionerId={practitionerId!}
        practiceId={popoverPracticeId}
        organizationId={organizationId}
        profileId={preferredProfile.id!}
        profileName={preferredProfile.profileName}
        onSelectSlot={onSelectSlot ? (slot) => onSelectSlot(slot, preferredProfile) : undefined}
      >
        <span className="text-brand-primary underline decoration-dotted underline-offset-2 cursor-pointer hover:decoration-solid">
          {slotsToday} slots
        </span>
      </SlotSelectionPopover>
    ) : noPracticeProfile ? (
      <span data-no-practice-profile className="text-text-secondary">{NO_PRACTICE_PROFILE_TEXT}</span>
    ) : (
      <span className="text-brand-primary">{slotsToday} slots</span>
    );
  const nextEls = nextSlots.map((s, i) => {
    // Each "Next" chip becomes a quick-create trigger when we have the raw slot + a preferred profile + onSelectSlot
    // callback (PROD-829 path B, Flutter `calendar_tab.dart:4514, 4552, 4615`). Otherwise stays as plain text.
    const rawSlot = nextSlotResponses?.[i];
    const canQuickCreate =
      !!rawSlot && !!preferredProfile && !!onSelectSlot && !!rawSlot.start && !!rawSlot.end;
    const pick = (e: React.MouseEvent) => {
      e.stopPropagation();
      // Convert AppointmentSlot → SlotResponse shape for the callback (matches the popover trigger's contract).
      onSelectSlot!(
        { id: '', start: rawSlot!.start!, end: rawSlot!.end!, comment: rawSlot!.comment },
        preferredProfile!,
      );
    };
    return { s, canQuickCreate, pick };
  });
  const quickBookEl = (
    <div className={cn('flex gap-2', layout === 'strip' && 'mt-1')}>
      {quickBookGroups.map((g, i) => (
        <QuickBookChip
          key={`${g.location}-${i}`}
          group={g}
          onSelectProfile={(profile) => onSelectProfile?.(g, profile)}
        />
      ))}
    </div>
  );

  // The hover card (Antonin, 13 Oct): the strip's mini chip reveals this cleaner version. Header (photo, name,
  // specialty, issue, ✓), then labelled rows: Today, Next (each slot a quiet outline chip that books it), Book.
  // The hover card is a clinic access badge (Antonin, 13 Oct: "more spacious, more immersive, cooler, better
  // hierarchy, more convivial … the new DS Okabe-Ito colours for the icons … picture a bit bigger … remind a clinic
  // access badge … a subtle stamp of the clinic with a blend effect"). A tinted band with the lanyard slot and the
  // clinic's seal (blended, faint), the photo overlapping it, then who and whether on shift, then today in three
  // figures, the next slots, and the bookings as soft tiles in the muted Okabe-Ito category colours.
  if (layout === 'detail') {
    const label = 'text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary';
    const firstNext = nextEls[0]?.s;
    const bigSlots = slotsUnknown ? (
      <span className="text-[length:var(--type-lead-size)] font-semibold leading-[var(--type-lead-line)] tabular-nums text-text-muted">—</span>
    ) : slotPopoverEnabled && preferredProfile && popoverPracticeId && organizationId ? (
        <SlotSelectionPopover
          practitionerId={practitionerId!}
          practiceId={popoverPracticeId}
          organizationId={organizationId}
          profileId={preferredProfile.id!}
          profileName={preferredProfile.profileName}
          onSelectSlot={onSelectSlot ? (slot) => onSelectSlot(slot, preferredProfile) : undefined}
        >
          <span className="cursor-pointer text-[length:var(--type-lead-size)] font-semibold leading-[var(--type-lead-line)] tabular-nums text-brand-primary underline decoration-dotted decoration-2 underline-offset-4 hover:decoration-solid">
            {slotsToday}
          </span>
        </SlotSelectionPopover>
      ) : (
        <span className="text-[length:var(--type-lead-size)] font-semibold leading-[var(--type-lead-line)] tabular-nums text-text-primary">{slotsToday}</span>
      );
    const noProfileEl = slotsFailed ? (
      <p className="mx-6 mt-2 text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)]">{slotsErrorEl}</p>
    ) : noPracticeProfile ? (
      <p data-no-practice-profile className="mx-6 mt-2 text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)] text-text-secondary">
        {NO_PRACTICE_PROFILE_TEXT}
      </p>
    ) : null;
    return (
      <div data-provider-detail data-provider-badge className="relative w-[352px] overflow-hidden rounded-2xl border border-border-default bg-surface-card text-left">
        {/* The band: brand-blue wash, the lanyard slot, the clinic's seal pressed into it. */}
        <div aria-hidden className="relative h-[80px] overflow-hidden" style={{ background: 'radial-gradient(120% 140% at 0% 0%, color-mix(in srgb, var(--color-brand-primary) 16%, white) 0%, var(--color-blue-100) 45%, color-mix(in srgb, var(--color-blue-100) 35%, white) 100%)' }}>
          <span className="absolute left-1/2 top-3 h-2 w-10 -translate-x-1/2 rounded-full bg-[var(--color-surface-card)] shadow-[inset_0_1px_2px_rgba(21,27,32,0.18)]" />
          <ClinicStamp
            practice={practiceName ?? 'Scrips clinic'}
            size={148}
            className="absolute -right-6 -top-10 text-brand-primary"
            style={{ opacity: 0.16, mixBlendMode: 'multiply', transform: 'rotate(-14deg)' }}
          />
        </div>
        <div className="relative -mt-12 flex items-end gap-4 px-6">
          <div className="relative flex-shrink-0 rounded-[24px] bg-surface-card p-1 shadow-[0_2px_8px_rgba(21,27,32,0.10)]">
            <PatientAvatar name={name} photoUrl={photo} variant="provider" size={72} className="rounded-[16px] [&>*]:rounded-[16px]" />
            {slotsUnknown ? null : (
              <span
                aria-label={available ? 'Available' : 'Unavailable'}
                className={cn('absolute bottom-1 right-1 h-4 w-4 rounded-full border-[3px] border-surface-card', available ? 'bg-status-success' : 'bg-grey-400')}
              />
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 px-6 pt-3">
          <div className="flex min-w-0 items-center gap-2 [&_button]:text-[length:var(--type-card-title-size)] [&_button]:leading-[var(--type-card-title-line)] [&_button]:font-semibold [&_p]:text-[length:var(--type-card-title-size)] [&_p]:leading-[var(--type-card-title-line)] [&_p]:font-semibold">
            {nameEl}{issueEl}
            <span className="ml-auto flex items-center">{checkboxEl}</span>
          </div>
          {specialty ? <p className="truncate text-[length:var(--type-body-size)] leading-[var(--type-body-line)] text-text-secondary">{specialty}</p> : null}
          {slotsUnknown ? null : (
            <p className="mt-1 inline-flex items-center gap-2 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)]">
              <span aria-hidden className={cn('h-2 w-2 rounded-full', available ? 'bg-status-success' : 'bg-grey-400')} />
              <span className={available ? 'text-[var(--color-green-700)]' : 'text-text-secondary'}>{available ? 'On shift' : 'Off today'}</span>
              {schedule ? <span className="font-normal tabular-nums text-text-secondary">· {schedule}</span> : null}
            </p>
          )}
        </div>
        {/* Today, in three figures. */}
        <div className="mx-6 mt-4 grid grid-cols-2 divide-x divide-border-subtle rounded-xl bg-[var(--color-grey-100)]">
          <div className="flex flex-col items-start gap-1 px-3 py-2.5 [&>*]:text-left">
            <span className={label}>Free today</span>
            {bigSlots}
          </div>
          <div className="flex flex-col gap-1 px-3 py-2.5">
            <span className={label}>Next</span>
            <span className="text-[length:var(--type-lead-size)] font-semibold leading-[var(--type-lead-line)] tabular-nums text-text-primary">{firstNext ?? '—'}</span>
          </div>
        </div>
        {noProfileEl}
        <div className="flex flex-col gap-4 px-6 pb-6 pt-4">
          <section className="flex flex-col gap-2">
            <h4 className={label}>Next slots</h4>
            {slotsUnknown ? (
              <span className="text-[length:var(--type-body-size)] leading-[var(--type-body-line)] text-text-muted">—</span>
            ) : nextEls.length === 0 ? (
              <span className="text-[length:var(--type-body-size)] leading-[var(--type-body-line)] text-text-muted">No slots available today</span>
            ) : (
              <div className="flex flex-wrap gap-2">
                {nextEls.map(({ s, canQuickCreate, pick }, i) =>
                  canQuickCreate ? (
                    <button key={i} type="button" onClick={pick} title="Book this slot"
                      className="h-8 rounded-lg border border-border-default bg-surface-card px-3 text-[length:var(--type-body-strong-size)] font-medium leading-[var(--type-body-strong-line)] tabular-nums text-text-primary transition-colors hover:border-brand-primary hover:bg-[var(--color-blue-100)] hover:text-brand-primary">
                      {s}
                    </button>
                  ) : (
                    <span key={i} className="inline-flex h-8 items-center rounded-lg bg-grey-100 px-3 text-[length:var(--type-body-strong-size)] font-medium tabular-nums text-text-secondary">{s}</span>
                  ),
                )}
              </div>
            )}
          </section>
          {quickBookGroups.length > 0 ? (
            <section className="flex flex-col gap-2">
              <h4 className={label}>Book</h4>
              <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(3, quickBookGroups.length)}, minmax(0, 1fr))` }}>
                {quickBookGroups.map((g, i) => {
                  const preferred = g.profiles.find((p) => p.isPreferred) ?? g.profiles[0];
                  if (!preferred) return null;
                  const loc = (g.location || 'office').toLowerCase();
                  const hue = ({ office: 'jade', virtual: 'sky', home: 'orange', block: 'purple' } as Record<string, string>)[loc] ?? 'jade';
                  const LOCATION_LABEL: Record<string, string> = { office: 'In clinic', virtual: 'Video', home: 'Home', block: 'Block' };
                  return (
                    <button
                      key={`${g.location}-${i}`}
                      type="button"
                      onClick={() => (onBookLocation ?? onSelectProfile)?.(g, preferred)}
                      className="flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(21,27,32,0.10)]"
                      style={{
                        backgroundColor: `var(--cat-${hue}-100)`,
                        borderColor: `color-mix(in srgb, var(--cat-${hue}-200) 70%, transparent)`,
                        color: `var(--cat-${hue}-800)`,
                      }}
                    >
                      <ScripsIcon name={({ office: 'office', virtual: 'virtual', home: 'home', block: 'block' } as Record<string, ScripsIconName>)[loc] ?? 'office'} size={16} style={{ color: `var(--cat-${hue}-600)` }} />
                      <span className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)]">{LOCATION_LABEL[loc] ?? g.location}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}
        </div>
        {/* The badge's foot, under a perforation: whose clinic this card belongs to. */}
        <div className="flex items-center gap-2 border-t border-dashed border-border-default bg-[color-mix(in_srgb,var(--color-blue-100)_35%,white)] px-6 py-3">
          <ClinicStamp practice="" size={18} className="shrink-0 text-brand-primary" />
          <span className="truncate text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-primary">{practiceName ?? 'Scrips clinic'}</span>
          <span className="ml-auto text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)] text-text-secondary">Care team</span>
        </div>
      </div>
    );
  }

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'flex-shrink-0 flex items-center gap-3 min-w-[200px] text-left',
        onClick && 'cursor-pointer',
      )}
    >
      <div className="relative">
        <PatientAvatar
          name={name}
          photoUrl={photo}
          variant="provider"
          size={40}
        />
        {slotsUnknown ? null : (
          <AvailabilityDot
            available={available}
            className="absolute bottom-0 right-0"
          />
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          {nameEl}
          {issueEl}
          {checkboxEl}
        </div>
        <p className="text-xs text-text-secondary leading-snug">
          {slotsUnknown && !schedule ? null : <>{schedule} · </>}{slotsEl}{noPracticeProfile || slotsFailed ? null : ' today'}
        </p>
        <p className="text-xs text-text-secondary leading-snug">
          Next:{' '}
          {slotsUnknown ? (
            <span className="text-text-muted">—</span>
          ) : nextEls.length === 0 ? (
            <span className="text-text-muted">No slots available</span>
          ) : (
            nextEls.map(({ s, canQuickCreate, pick }, i) => (
              <span key={i}>
                {i > 0 && ' · '}
                {canQuickCreate ? (
                  <button
                    type="button"
                    onClick={pick}
                    className="text-brand-primary underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer"
                  >
                    {s}
                  </button>
                ) : (
                  <span className="text-brand-primary">{s}</span>
                )}
              </span>
            ))
          )}
        </p>
        {quickBookEl}
      </div>
    </div>
  );
}
